<template>
  <div
    ref="carousel"
    class="c-card-carousel pa-3"
  >
    <div
      ref="swiperEl"
      class="swiper"
      :style="{ '--swiper-pagination-bottom': `${paginationBottom}px` }"
    >
      <div class="swiper-wrapper">
        <div
          v-for="item in virtualItems"
          :key="item.id"
          class="c-slide swiper-slide"
          :class="cardHeight > 0 ? 'c-fixed' : 'c-auto'"
          :virtual-index="item.index"
          :style="{ left: `${virtualData.offset}px`, width: `${slideWidth}px`, marginRight: `${spaceBetween}px` }"
        >
          <slot name="card" :item="item" />
        </div>
      </div>
      <div v-show="isPaginationEnabled" class="swiper-pagination" />
      <div v-show="isScrollbarEnabled" class="swiper-scrollbar"/>
      <div
        v-if="showPagination"
        class="c-navigation d-flex justify-space-between mt-2"
        :class="{ 'mb-4': showScrollbar }"
      >
        <v-btn
          class="c-button c-button-prev"
          :disabled="isBeginning && !rewind"
          fab
          x-small
          @click="prev()"
        >
          <v-icon dark large> mdi-chevron-left </v-icon>
        </v-btn>
        <v-btn
          class="c-button c-button-next"
          :disabled="isEnd && !rewind"
          fab
          x-small
          @click="next()"
        >
          <v-icon dark large> mdi-chevron-right </v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import Swiper from 'swiper'
import { Pagination, Scrollbar, Virtual } from 'swiper/modules'
// import 'swiper/swiper-bundle.min.css' // all css (core + modules)
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/scrollbar'
import 'swiper/css/virtual'
import mobileMixin from '@/mixins/mobileMixin.js'

//Swiper.use([Controller, Navigation, Pagination, Virtual])

export default {
  name: 'CardCarousel',

  components: {},

  mixins: [mobileMixin],

  props: {
    items: {
      type: Array,
      required: true
    },

    cardHeight: {
      type: Number,
      required: false,
      default: 0
    },

    cardWidth: {
      type: Number,
      required: false,
      default: 300
    },

    loading: {
      type: Boolean,
      required: false,
      default: false
    },

    width: {
      type: Number,
      required: false,
      default: 0
    }
  },

  data: function () {
    return {
      isActivated: false,
      swiperId: this._uid,
      swiper: null,
      bulletThreshold: 8,
      carouselWidth: this.width || 0,
      rewind: true,
      spaceBetween: 12,
      modules: [Pagination, Scrollbar, Virtual],
      virtualData: {
        offset: 0,
        slides: []
      }
    }
  },

  computed: {
    isReallyMobile() {
      return this.mobileMixin_isReallyMobile
    },

    cols() {
      const width = this.carouselWidth

      if (width > 2400) return 1 // 12 columns
      if (width > 1800) return 2 // 6 columns
      if (width > 1200) return 3 // 4 columns
      if (width > 900) return 4 // 3 columns
      if (width > 600) return 6 // 2 columns
      if (width > 0) return 12 // 1 column
      return 12
    },

    /* container */

    breakpoints() {
      // prettier-ignore
      return {
           0: { slidesPerView:  1 },
         600: { slidesPerView:  2 },
         900: { slidesPerView:  3 },
        1200: { slidesPerView:  4 },
        1800: { slidesPerView:  6 },
        2400: { slidesPerView: 12 }
      }
    },

    containerClass() {
      return `c-swiper-${this.swiperId}-container`
    },

    /* slides */

    slideClass() {
      return `c-swiper-${this.swiperId}-slide`
    },

    slideStyle() {
      return Object.entries(this.breakpoints)
        .map(([key, value]) => {
          const { slidesPerView = 1 } = value
          const spaceBetween = this.spaceBetween
          return `
             @media (min-width: ${key}px) {
                .${this.slideClass} {
                  width: calc((100% - ${(slidesPerView - 1) * spaceBetween}px) / ${slidesPerView})
                  margin-right: ${spaceBetween}px;
              }
            }
          `
        })
        .join(' ')
        .replace(/\s+/g, ' ')
    },

    slideWidth() {
      return (
        (this.carouselWidth - (this.slidesPerView - 1) * this.spaceBetween) / this.slidesPerView
      )
    },

    slidesPerGroup() {
      // return 1
      // return this.slidesPerView
      return this.slidesPerView > 1 ? this.slidesPerView - 1 : 1
    },

    slidesPerView() {
      return 12 / this.cols
    },

    /* navigation */

    isBeginning() {
      return this.swiper?.isBeginning
    },

    isEnd() {
      return this.swiper?.isEnd
    },

    /* pagination */

    isPaginationEnabled() {
      return this.swiper?.params?.pagination.enabled
    },

    paginationBottom() {
      return this.showScrollbar ? 0 : 4
    },

    paginationType() {
      return this.items.length > this.bulletThreshold ? 'custom' : 'bullets'
    },

    showPagination() {
      // const allSlidesVisible = this.swiper?.isBeginning && this.swiper?.isEnd
      const needsPagination = this.items?.length > this.swiper?.params.slidesPerView
      return !this.loading && needsPagination
    },

    /* scrollbar */

    isScrollbarEnabled() {
      return this.swiper?.params?.scrollbar.enabled
    },

    showScrollbar() {
      const needsScrollbar = this.items.length > this.bulletThreshold
      return !this.loading && needsScrollbar
    },

    /* virtualization */

    isVirtualEnabled() {
      return this.swiper?.params?.virtual.enabled
    },

    virtualItems() {
      return this.virtualData.slides
    }
  },

  watch: {
    items: {
      immediate: false,
      deep: false,
      handler: function (newItems, _oldItems) {
        if (this.swiper) {
          this.swiper.virtual.slides = newItems
          this.$nextTick(() => {
            this.updateSwiper()
            this.goToSlide(0)
          })
        }
      }
    },

    paginationType: {
      immediate: false,
      handler: function (newType, _oldType) {
        if (this.swiper) {
          const pagination = this.swiper.pagination
          const paginationEl = this.swiper.pagination.el
          const paginationParams = this.swiper.params.pagination

          paginationParams.type = newType
          if (newType === 'custom') {
            if (paginationEl) {
              paginationEl.classList.remove('swiper-pagination-bullets-dynamic')
              paginationEl.style.width = ''
            }
          }

          // rebuild pagination
          pagination.destroy()
          pagination.init()
          pagination.render()
          pagination.update()
        }
      }
    },

    showPagination: {
      immediate: false, // wait for swiper to mount
      handler: function (newValue, _oldValue) {
        if (this.swiper) {
          newValue ? this.swiper.pagination.enable() : this.swiper.pagination.disable()
          this.swiper.params.pagination.enabled = newValue
          // this.swiper.pagination.update()
        }
      }
    },

    showScrollbar: {
      immediate: false, // wait for swiper to mount
      handler: function (newValue, _oldValue) {
        if (this.swiper) {
          newValue ? this.swiper.scrollbar.enable() : this.swiper.scrollbar.disable()
          this.swiper.params.scrollbar.enabled = newValue
          // this.swiper.scrollbar.updateSize()
        }
      }
    },

    width: {
      immediate: false,
      handler: function (newWidth, _oldWidth) {
        if (newWidth !== this.carouselWidth) {
          this.carouselWidth = newWidth || this.$refs.carousel.clientWidth
          this.resetSlidesPerView()
          this.swiper?.update()
        }
      }
    }
  },

  created: function () {},

  mounted: function () {
    this.swiperEl = this.$refs.swiperEl
    this.carouselWidth = this.width || this.$refs.carousel.clientWidth
    this.swiper = this.createSwiper(this.swiperEl)
  },

  activated: function () {
    this.isActivated = true
    if (this.swiper.destroyed) {
      this.swiper = this.createSwiper(this.swiperEl)
    }
  },

  deactivated: function () {
    this.isActivated = false
  },

  beforeDestroy: function () {
    this.destroySwiper()
  },

  methods: {
    /* lifecycle methods */

    createSwiper(swiperEl) {
      const swiperParams = {
        // core settings
        enabled: true,
        cache: true,
        cssMode: false, // "true" causes pagination grief
        edgeSwipeDetection: 'prevent',
        initialSlide: 0,
        init: false,
        observer: false,
        observeParents: false,
        observeSlideChildren: false,
        rewind: this.rewind,
        slidesPerView: this.slidesPerView,
        slidesPerGroup: 1,
        spaceBetween: 12,
        touchMoveStopPropagation: true,

        // modules
        modules: this.modules,
        navigation: false,
        pagination: {
          enabled: this.showPagination,
          type: this.paginationType,
          clickable: true,
          dynamicBullets: false,
          dynamicMainBullets: this.bulletThreshold,
          el: '.swiper-pagination',
          formatFractionCurrent: this.formatFractionCurrent,
          formatFractionTotal: this.formatFractionTotal,
          renderCustom: this.renderCustom
        },
        scrollbar: {
          enabled: this.showScrollbar,
          draggable: true,
          el: '.swiper-scrollbar'
        },
        virtual: {
          enabled: true,
          addSlidesBefore: 5,
          addSlidesAfter: 5,
          cache: false,
          renderExternal: this.renderExternal,
          renderExternalUpdate: true,
          slides: this.items,
          slidesPerViewAutoSlideSize: this.slideWidth
        },

        // events
        on: {
          init: this.onInit
        }
      }

      const swiper = new Swiper(swiperEl, swiperParams)

      swiper.init(swiperEl)

      return swiper
    },

    updateSwiper() {
      // virtual update
      this.swiper?.virtual.update(true) // updates state of virtual slides
      // full update
      this.swiper?.update() // updates size/number of slides & adds/removes classes
    },

    destroySwiper() {
      const destroyInstance = true
      const cleanStyles = true
      this.swiper?.destroy(destroyInstance, cleanStyles)
    },

    /* event handlers */

    onInit(_swiper) {
      // console.error('initialized!', swiper)
    },

    onVirtualUpdate(_swiper) {
      // console.error('virtualUpdate!', swiper)
    },

    /* navigation */

    goToSlide(slideNum) {
      this.swiper?.slideTo(slideNum, 50, true)
    },

    next() {
      this.swiper.slideNext()
    },

    prev() {
      this.swiper.slidePrev()
    },

    resetSlidesPerView() {
      if (this.swiper) {
        if (this.swiper.params.slidesPerView !== this.slidesPerView) {
          this.swiper.params.slidesPerView = this.slidesPerView
          this.swiper.params.slidesPerGroup = this.slidesPerGroup
        }
      }
    },

    /* pagination */

    formatFractionCurrent(n) {
      return this.slidesPerView > 1 ? `${n} - ${n + this.slidesPerView}` : n
    },

    formatFractionTotal(n) {
      return this.slidesPerView > 1 ? n + this.slidesPerView : n
    },

    renderBullet(index, className) {
      // note: has no effect on dynamic bullets
      return `<span class="${className}">${index + 1}</span>`
    },

    renderFraction(currentClass, totalClass) {
      return `
        <span class="${currentClass}"></span>
        /
        <span class="${totalClass}"></span>
      `
    },

    renderProgressbar(progressbarFillClass) {
      return `<span class="${progressbarFillClass}"></span>`
    },

    renderCustom(swiper, current, total) {
      let customPaginationHtml = ''
      if (this.slidesPerView > 1) {
        const rangeStart = current
        const rangeEnd =
          current + this.slidesPerView > this.items.length
            ? this.items.length
            : current + this.slidesPerView - 1
        const numerator = this.slidesPerView > 1 ? `${rangeStart} - ${rangeEnd}` : `${rangeStart}`
        const denominator = `${this.items.length}`
        customPaginationHtml = `
          <span>${numerator} / ${denominator}</span>
        `
      } else {
        customPaginationHtml = `
          <span>${current} / ${total}</span>
        `
      }
      return customPaginationHtml
    },

    /* virtualization */

    renderExternal(data) {
      // data = { to, from, offset, slides }
      this.virtualData = data
    }
  }
}
</script>

<style lang="css" scoped>
.c-card-carousel {
  width: 100%;
  /* contain scroll events so swiping doesn't open drawers */
  overscroll-behavior-x: contain;
}
.c-button:hover .v-icon::before {
  color: var(--v-accent-base);
  opacity: 1;
  transform: scale(1.25);
}

/* swiper container */
.swiper {
  /* general */
  --swiper-theme-color: #ababab;
  --swiper-preloader-color: var(--swiper-theme-color);
  /* navigation */
  --swiper-navigation-color: var(--v-icon-base);
  --swiper-navigation-size: 36px;
  --swiper-navigation-top-offset: 10px;
  --swiper-navigation-sides-offset: 10px;
  /* pagination */
  --swiper-pagination-bottom: 4px;
  --swiper-pagination-top: auto;
  --swiper-pagination-color: var(--v-accent-base);
  --swiper-pagination-bullet-border-radius: 50%;
  --swiper-pagination-bullet-horizontal-gap: 4px;
  --swiper-pagination-bullet-vertical-gap: 4px;
  --swiper-pagination-bullet-size: 24px;
  --swiper-pagination-bullet-opacity: 1;
  --swiper-pagination-bullet-inactive-color: #000000;
  --swiper-pagination-bullet-inactive-opacity: 0.2;
  /* scrollbar */
  --swiper-scrollbar-sides-offset: 48px;
  --swiper-scrollbar-bottom: 28px;
  --swiper-scrollbar-top: auto;
  --swiper-scrollbar-drag-bg-color: var(--v-accent-base);
  --swiper-scrollbar-border-radius: 16px;
  --swiper-scrollbar-size: 8px;

  padding-top: 0px;
  max-width: 100%;
  max-height: 100%;
  min-height: 0;
  min-width: 0;
}

/* swiper wrapper */
.swiper-wrapper {
  align-items: stretch;
  box-sizing: border-box;
}

/* swiper slides */
.c-slide {
  box-sizing: border-box;
}

.c-slide.c-auto {
  height: auto;
}

.c-slide.c-fixed {
  height: 500px;
}

.c-slide > :deep(*) {
  box-sizing: border-box;
  margin: auto;
}

/* pagination */
.swiper-pagination {
  max-width: calc(100% - 96px);
  height: 24px;
  left: 48px;
}

/* scrollbar */
.swiper-scrollbar {
  cursor: grab;
}

.swiper-scrollbar.swiper-scrollbar-horizontal {
  outline: 0.5px solid var(--v-accent-base);
}
</style>
